import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  Clock,
  CheckCircle,
  AlertTriangle,
  FileText,
  Building,
  Phone,
  Mail,
  MapPin,
  Shield,
  Star,
  TrendingUp,
  Users,
  DollarSign,
  MoreHorizontal,
  RefreshCw,
  HelpCircle,
  ArrowDownRight,
  ArrowUpRight
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import ConfirmationModal from '../common/ConfirmationModal';
import VendorService from '../../services/vendorService';

// Custom Dropdown Component - exactly like Task Management
// Project-style Action Dropdown (Vendor Onboarding)
const ActionDropdown = ({ vendor, onView, onEdit, onDelete, onApprove }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setOpenUpward(window.innerHeight - rect.bottom < 120);
    }
    setIsOpen(prev => !prev);
  };

  return (
    <div
      ref={dropdownRef}
      className="relative"
      style={{ zIndex: isOpen ? 10000 : 'auto' }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-[var(--gold-primary)] rounded hover:bg-[#FFF9E6] hover:border-[var(--gold-primary)] transition-colors shadow-sm"
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight -
              buttonRef.current.getBoundingClientRect().top +
              8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                onClick={() => { setIsOpen(false); onView(vendor); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View
              </button>
            </li>

            {vendor.statusValue === 'Pending Approval' && (
              <li>
                <button
                  onClick={() => { setIsOpen(false); onApprove(vendor); }}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-green-700 hover:bg-green-50 text-left"
                >
                  <CheckCircle size={16} className="mr-2.5 text-green-500" />
                  Approve
                </button>
              </li>
            )}
            <li>
              <button
                onClick={() => { setIsOpen(false); onEdit(vendor); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit
              </button>
            </li>

            <li>
              <button
                onClick={() => { setIsOpen(false); onDelete(vendor); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
              >
                <Trash2 size={16} className="mr-2.5 text-red-500" />
                Delete
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

// Transform backend vendor data to frontend format
const transformVendorToOnboarding = (vendor) => {
  // Map status from backend to display format
  const statusMap = {
    'Active': 'Approved',
    'Inactive': 'Rejected',
    'Suspended': 'Suspended',
    'Blacklisted': 'Rejected',
    'Pending Approval': 'Under Review'
  };

  // Calculate compliance score (simplified - can be enhanced)
  let complianceScore = 50; // Default
  if (vendor.compliance) {
    let score = 0;
    let maxScore = 0;

    // Certifications (30 points)
    if (vendor.compliance.certifications && vendor.compliance.certifications.length > 0) {
      score += Math.min(30, vendor.compliance.certifications.length * 5);
    }
    maxScore += 30;

    // Insurance (20 points)
    if (vendor.compliance.insurance && vendor.compliance.insurance.policyNumber) {
      score += 20;
    }
    maxScore += 20;

    // Business registration (20 points)
    if (vendor.businessRegistration && vendor.businessRegistration.taxId) {
      score += 20;
    }
    maxScore += 20;

    // Primary contact (15 points)
    if (vendor.primaryContact && vendor.primaryContact.email) {
      score += 15;
    }
    maxScore += 15;

    // Addresses (15 points)
    if (vendor.addresses && vendor.addresses.headquarters) {
      score += 15;
    }
    maxScore += 15;

    complianceScore = maxScore > 0 ? Math.round((score / maxScore) * 100) : 50;
  }

  // Calculate document counts (simplified)
  let documentsSubmitted = 0;
  let documentsApproved = 0;

  if (vendor.businessRegistration) {
    if (vendor.businessRegistration.taxId) documentsSubmitted++;
    if (vendor.businessRegistration.registrationNumber) documentsSubmitted++;
    if (vendor.businessRegistration.taxId) documentsApproved++;
    if (vendor.businessRegistration.registrationNumber) documentsApproved++;
  }
  if (vendor.compliance?.insurance?.policyNumber) {
    documentsSubmitted++;
    documentsApproved++;
  }
  if (vendor.compliance?.certifications?.length > 0) {
    documentsSubmitted += vendor.compliance.certifications.length;
    documentsApproved += vendor.compliance.certifications.length;
  }
  if (vendor.primaryContact?.email) {
    documentsSubmitted++;
    documentsApproved++;
  }

  // Get contact person name
  const contactPerson = vendor.primaryContact?.name || 'N/A';
  const email = vendor.primaryContact?.email || vendor.communication?.email || 'N/A';

  // Get location
  const location = vendor.addresses?.headquarters
    ? `${vendor.addresses.headquarters.city || ''}${vendor.addresses.headquarters.city && vendor.addresses.headquarters.state ? ', ' : ''}${vendor.addresses.headquarters.state || ''}`
    : 'N/A';

  return {
    id: vendor.vendorId || vendor._id,
    _id: vendor._id,
    vendorId: vendor.vendorId,
    companyName: vendor.name || '',
    contactPerson: contactPerson,
    email: email,
    phone: vendor.primaryContact?.phone || vendor.communication?.phone || '',
    category: vendor.industry || vendor.sector || 'Other',
    status: statusMap[vendor.status] || vendor.status || 'Under Review',
    statusValue: vendor.status, // Keep original for filtering
    registrationDate: vendor.createdAt ? new Date(vendor.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    approvalDate: vendor.status === 'Active' ? (vendor.updatedAt ? new Date(vendor.updatedAt).toISOString().split('T')[0] : null) : null,
    documentsSubmitted: documentsSubmitted,
    documentsApproved: documentsApproved,
    complianceScore: complianceScore,
    location: location,
    businessLicense: vendor.businessRegistration?.registrationNumber ? 'Valid' : 'Pending',
    insuranceStatus: vendor.compliance?.insurance?.policyNumber ? 'Active' : 'Pending',
    certifications: vendor.compliance?.certifications?.map(cert => cert.name) || [],
    rating: vendor.performance?.overallRating || 3.5,
    vendorData: vendor // Keep full data for reference
  };
};

const VendorOnboarding = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    itemsPerPage: 10,
    totalItems: 0,
    current: 1,
    pages: 1,
    limit: 10
  });

  useEffect(() => {
    loadVendors();
  }, [pagination.page, searchTerm, statusFilter, categoryFilter]);

  // Debounce search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (pagination.page === 1) {
        loadVendors();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const loadVendors = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page || 1,
        limit: pagination.itemsPerPage || 10,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(categoryFilter && { industry: categoryFilter })
      };

      const response = await VendorService.getVendors(params);

      if (response && response.success) {
        const transformedVendors = (response.data || []).map(transformVendorToOnboarding);
        setVendors(transformedVendors);

        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            current: response.pagination.current || response.pagination.page || 1,
            pages: response.pagination.pages || 1,
            totalItems: response.pagination.total || 0,
            limit: response.pagination.limit || 10
          }));
        }
      } else {
        setVendors([]);
        if (response && !response.data) {
          showError('Failed to load vendors.');
        }
      }
    } catch (err) {
      console.error('Error loading vendors:', err);
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to load vendors.';
      showError(errorMessage);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadVendors();
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/vendor-onboarding';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh vendors list',
      disabled: loading
    },
    {
      label: 'Register Vendor',
      icon: <Plus size={18} />,
      onClick: () => navigate('/subcontracting/onboarding/create'),
      variant: 'primary',
      tooltip: 'Register a new vendor'
    }
  ];

  const handleView = (vendor) => {
    const vendorId = vendor._id || vendor.id;
    if (vendorId) {
      navigate(`/subcontracting/onboarding/view/${vendorId}`);
    } else {
      console.error('Vendor ID not found:', vendor);
      showError('Unable to view vendor: ID not found');
    }
  };

  const handleEdit = (vendor) => {
    const vendorId = vendor._id || vendor.id;
    if (vendorId) {
      navigate(`/subcontracting/onboarding/edit/${vendorId}`);
    } else {
      console.error('Vendor ID not found:', vendor);
      showError('Unable to edit vendor: ID not found');
    }
  };

  const handleApprove = async (vendor) => {
    try {
      const vendorId = vendor._id || vendor.id;
      const response = await VendorService.updateVendor(vendorId, { status: 'Active' });
      if (response && response.success) {
        success('Vendor approved successfully!');
        await loadVendors();
      } else {
        showError(response?.message || 'Failed to approve vendor.');
      }
    } catch (err) {
      console.error('Error approving vendor:', err);
      showError(err?.message || 'Failed to approve vendor.');
    }
  };

  const handleDelete = (vendor) => {
    setVendorToDelete(vendor);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!vendorToDelete) return;

    setIsDeleting(true);
    try {
      const vendorId = vendorToDelete._id || vendorToDelete.id;
      if (!vendorId) {
        throw new Error('Vendor ID not found');
      }

      await VendorService.deleteVendor(vendorId);
      success('Vendor deleted successfully!');
      await loadVendors();
      setShowDeleteModal(false);
      setVendorToDelete(null);
    } catch (err) {
      console.error('Error deleting vendor:', err);
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to delete vendor.';
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const capitalizeFirst = (value = '') =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Approved': { color: '#10b981', bg: '#d1fae5', text: 'Approved' },
      'Under Review': { color: '#f59e0b', bg: '#fef3c7', text: 'Under Review' },
      'Pending Approval': { color: '#f59e0b', bg: '#fef3c7', text: 'Pending Approval' },
      'Pending Documents': { color: '#dc2626', bg: '#fee2e2', text: 'Pending Documents' },
      'Rejected': { color: '#ef4444', bg: '#fee2e2', text: 'Rejected' },
      'Suspended': { color: '#6b7280', bg: '#f3f4f6', text: 'Suspended' },
      'Inactive': { color: '#6b7280', bg: '#f3f4f6', text: 'Inactive' }
    };

    const config = statusConfig[status] || {
      color: '#6b7280',
      bg: '#f3f4f6',
      text: status
    };

    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          border: `1px solid ${config.color}20`,
          whiteSpace: 'nowrap'
        }}
      >
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: config.color }}
        />
        {config.text}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const categoryConfig = {
      'Technology': { color: '#2563eb', bg: '#dbeafe' },
      'Construction': { color: '#92400e', bg: '#fef3c7' },
      'Consulting': { color: '#7c3aed', bg: '#ede9fe' },
      'Manufacturing': { color: '#0f766e', bg: '#ccfbf1' },
      'Healthcare': { color: '#be123c', bg: '#ffe4e6' },
      'Finance': { color: '#065f46', bg: '#d1fae5' },
      'Other': { color: '#6b7280', bg: '#f3f4f6' }
    };

    const config = categoryConfig[category] || categoryConfig['Other'];

    return (
      <span
        className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          border: `1px solid ${config.color}20`,
          whiteSpace: 'nowrap'
        }}
      >
        {category}
      </span>
    );
  };


  const getComplianceBadge = (score) => {
    if (score >= 90) return <span className="badge bg-success">Excellent</span>;
    if (score >= 70) return <span className="badge bg-warning">Good</span>;
    if (score >= 50) return <span className="badge bg-info">Fair</span>;
    return <span className="badge bg-danger">Poor</span>;
  };

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'companyName',
      label: 'Company',
      render: (value, vendor) => (
        <div>
          <div className="fw-bold text-truncate" style={{ maxWidth: '150px' }} title={vendor.companyName}>
            {vendor.companyName}
          </div>
          <small className="text-muted">{vendor.vendorId || vendor.id}</small>
        </div>
      )
    },
    {
      key: 'contactPerson',
      label: 'Contact',
      render: (value, vendor) => (
        <div>
          <div className="fw-medium text-truncate" style={{ maxWidth: '120px' }} title={vendor.contactPerson}>
            {vendor.contactPerson}
          </div>
          {/*<small className="text-muted text-truncate d-block" style={{ maxWidth: '120px' }} title={vendor.email}>
            {vendor.email}
          </small>*/}
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      render: (_, vendor) => getCategoryBadge(capitalizeFirst(vendor.category))
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, vendor) => getStatusBadge(vendor.status)
    },
    {
      key: 'complianceScore',
      label: 'Compliance',
      render: (value, vendor) => (
        <div>
          <div className="fw-medium text-truncate d-block" style={{ maxWidth: '100px' }} title={`${vendor.complianceScore}%`}>
            {vendor.complianceScore}%
          </div>
          {getComplianceBadge(vendor.complianceScore)}
        </div>
      )
    },
    {
      key: 'documentsSubmitted',
      label: 'Documents',
      render: (value, vendor) => (
        <div>
          <div className="fw-medium text-truncate d-block" style={{ maxWidth: '100px' }} title={`${vendor.documentsApproved}/${vendor.documentsSubmitted}`}>
            {vendor.documentsApproved}/{vendor.documentsSubmitted}
          </div>
          <small className="text-muted text-truncate d-block" style={{ maxWidth: '100px' }} title="Approved/Submitted">
            Approved/Submitted
          </small>
        </div>
      )
    },
    {
      key: 'registrationDate',
      label: 'Registration',
      render: (value, vendor) => (
        <div>
          <div className="fw-medium text-truncate d-block" style={{ maxWidth: '100px' }} title={new Date(vendor.registrationDate).toLocaleDateString()}>
            {new Date(vendor.registrationDate).toLocaleDateString()}
          </div>
          {vendor.approvalDate && (
            <small className="text-muted text-truncate d-block" style={{ maxWidth: '100px' }} title={`Approved: ${new Date(vendor.approvalDate).toLocaleDateString()}`}>
              Approved: {new Date(vendor.approvalDate).toLocaleDateString()}
            </small>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, vendor) => {
        if (!vendor || !vendor.id) {
          console.error('Invalid vendor data in render function:', vendor);
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <ActionDropdown
            vendor={vendor}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onApprove={handleApprove}
          />
        );
      }
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Vendor Onboarding"
        description="Streamlined vendor registration and approval process"
        actions={headerActions}
      />
      <div className="page-container">
        {/* KPI Cards */}
        {/* KPI Cards – EXACT Dashboard Style */}
        <div className="grid max-w-[1200px] mx-auto w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-8">

          {/* Total Vendors */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--gold-primary)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-[#059669]">
                <ArrowUpRight size={14} /> +6%
              </div>

              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FFF9E6]">
                <Building size={20} className="text-[var(--gold-primary)]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {pagination.totalItems || vendors.length}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Vendors
                </div>
              </div>
            </div>
          </div>

          {/* Approved Vendors */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--gold-primary)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-[#059669]">
                <ArrowUpRight size={14} /> +4%
              </div>

              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FFF9E6]">
                <CheckCircle size={20} className="text-[var(--gold-primary)]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {vendors.filter(v => v.status === 'Approved' || v.statusValue === 'Active').length}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Approved Vendors
                </div>
              </div>
            </div>
          </div>

          {/* Under Review */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--gold-primary)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 rounded-full bg-[#FEF3C7] px-3 py-1 text-[11px] font-semibold text-[#92400E]">
                Action Required
              </div>

              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FEF3C7]">
                <Clock size={20} className="text-[#92400E]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {vendors.filter(v => v.status === 'Under Review' || v.statusValue === 'Pending Approval').length}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Under Review
                </div>
              </div>
            </div>
          </div>

          {/* Pending Documents */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--gold-primary)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-[#DC2626]">
                <ArrowDownRight size={14} /> -1%
              </div>

              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FEE2E2]">
                <FileText size={20} className="text-[#DC2626]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {vendors.filter(v => v.documentsSubmitted > 0 && v.documentsApproved < v.documentsSubmitted).length}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Pending Documents
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vendors Table */}
        <DataTable
          data={vendors}
          columns={columns}
          title="Vendors"
          loading={loading}
          pagination={{
            currentPage: pagination.current || pagination.page || 1,
            totalPages: pagination.pages || 1,
            total: pagination.totalItems || 0,
            itemsPerPage: pagination.limit || pagination.itemsPerPage || 10
          }}
          onPageChange={(page) => {
            setPagination(prev => ({
              ...prev,
              page: page,
              current: page
            }));
          }}
          onSearchChange={(value) => {
            setSearchTerm(value);
          }}
          searchPlaceholder="Search vendors..."
          searchable={true}
          emptyMessage="No vendors found. Click 'Register Vendor' to get started."
          showActions={false}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Vendor"
        message={`Are you sure you want to delete "${vendorToDelete?.companyName || vendorToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default VendorOnboarding;
